import {Resolution} from "../../common/resolution";
import {BOOTSTRAP} from "../../common/resolutionConstants";
import {resolve} from "../../container";
import {ManagingResources} from "../../common/lifetime";
import type {Nullable} from "../../bootstrap/common/types";
import {customElement, property} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {PLUS_ICON} from "../../common/svgIcons";
import {unsafeSVG} from "lit/directives/unsafe-svg.js";
import {UnLitElement} from "../../common/elements";
import {Dialogs} from "./dialog/dialogs";
import {handleActivationKey} from "../../common/utils/events";

const CONVERSION_FACTOR = 12;
const PLUS_SIGN_WITH_CLASS = PLUS_ICON.replace("<svg", "<svg class='indicator-plus'");

export class Tile {
    private width: number;
    private height: number;
    private left: number;
    private top: number;
    private indicatorX: number;
    private indicatorY: number;

    public constructor(private tileElement: HTMLElement, private indicatorElement: Nullable<HTMLElement>, config: TileConfig) {
        this.width = config.width;
        this.height = config.height;
        this.left = config.left;
        this.top = config.top;
        this.indicatorX = config.indicatorX;
        this.indicatorY = config.indicatorY;
    }

    public layout(scale: number): void {
        this.tileElement.style.width = `${this.width * scale}px`;
        this.tileElement.style.height = `${this.height * scale}px`;
        this.tileElement.style.left = `${this.left * scale}px`;
        this.tileElement.style.top = `${this.top * scale}px`;

        const relativeY = `${(this.relativeIndicatorY(scale))}px`;
        const relativeX = `${(this.relativeIndicatorX(scale))}px`;
        this.tileElement.style.setProperty("--relative-indicator-y", relativeY);
        this.tileElement.style.setProperty("--relative-indicator-x", relativeX);

        if (this.indicatorElement) {
            this.indicatorElement.style.top = relativeY;
            this.indicatorElement.style.left = relativeX;
        }
    }

    private relativeIndicatorY(scale: number): number {
        return (this.indicatorY - this.top) * scale;
    }

    private relativeIndicatorX(scale: number): number {
        const padding = Number.parseInt(this.tileElement.computedStyle().padding) ?? 0;
        const paddingCorrection = this.isIndicatorOnLeftEdge() ? padding : -padding;
        return (this.indicatorX - this.left) * scale + paddingCorrection;
    }

    private isIndicatorOnLeftEdge(): boolean {
        return this.indicatorX === this.left;
    }

    public removeLayout(): void {
        this.tileElement.removeAttribute("style");
        this.indicatorElement?.removeAttribute("style");
    }
}

@customElement("eop-smart-image-heap")
export class EopSmartImageHeap extends ManagingResources(UnLitElement) {
    private heightPerColumnWidth: number;
    private tiles: Tile[] = [];

    public constructor(private resolution: Resolution = resolve(Resolution)) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        const config = this.getAttribute("config")!.parseAsJSON<TilesConfig>();
        const tileConfigs = config.tiles;
        this.heightPerColumnWidth = config.totalHeight;

        for (const tileConfig of tileConfigs) {
            const tile = this.querySelector("[data-id='" + tileConfig.id + "']");
            if (!tile) {
                continue;
            }
            const tileElement = tile.querySelector<HTMLElement>(".smart-image-heap-tile")!;
            const indicator = tile.querySelector<HTMLElement>(".indicator-link");

            this.tiles.push(new Tile(tileElement, indicator, tileConfig));
        }

        if (this.resolution.downTo(BOOTSTRAP.MD)) {
            this.layout();
        }

        this.resolution.onWindowResize(() => {
            if (this.resolution.downTo(BOOTSTRAP.MD)) {
                this.layout();
            } else {
                this.removeLayout();
            }
        }, this);
    }

    private layout(): void {
        const columnWidth = this.getClientWidth() / CONVERSION_FACTOR;
        this.style.height = `${this.heightPerColumnWidth * columnWidth}px`;
        this.tiles.forEach(tile => tile.layout(columnWidth));
        this.classList.remove("raw-tiles");
    }

    private removeLayout(): void {
        this.removeAttribute("style");
        this.tiles.forEach(tile => tile.removeLayout());
    }

    private getClientWidth(): number {
        return this.getBoundingClientRect().width; // this.clientWidth and this.computedStyle().width lead to unexpected errors
    }
}

export type TilesConfig = {
    tiles: TileConfig[];
    totalHeight: number;
};

export type TileConfig = {
    id: string;
    width: number;
    height: number;
    left: number;
    top: number;
    indicatorX: number;
    indicatorY: number;
};

@customElement("eop-smart-image-heap-dialog-indicator")
export class EopSmartImageHeapDialogIndicator extends UnLitElement {

    @property({attribute: "dialog-id"})
    public dialogId: string;
    @property({attribute: "style"})
    public linkStyle: string;
    @property()
    public label: string;
    @property({attribute: "title"})
    public buttonTitle: string;

    public constructor(private dialogs: Dialogs = resolve(Dialogs)) {
        super();
    }

    public render(): TemplateResult {
        return html`
            <button @click=${this.openDialog}
                    @keydown=${this.openDialogOnKeydown}
                    type=button
                    class="indicator-link"
                    title=${this.buttonTitle}
            >
                ${unsafeSVG(PLUS_SIGN_WITH_CLASS)}
            </button>`;
    }

    private openDialog(): void {
        void this.dialogs.open(this.dialogId);
    }

    private openDialogOnKeydown(e: KeyboardEvent): void {
        handleActivationKey(e, () => this.openDialog());
    }
}